import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Zap, Pause, Play, Edit, Trash2, AlertTriangle, Target, ArrowRight, Clock,
  ChevronRight, Search, RefreshCw, Workflow, Plus
} from 'lucide-react';
import { AutomationRule } from '../../types';
import { getTriggerLabel, getActionLabel, getFrequencyLabel, formatTimeAgo } from './automationHelpers';

// Platform icons
const GoogleAdsIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
    <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.84 3.18-1.74 4.1-.98.98-2.52 2.06-5.1 2.06-4.08 0-7.28-3.28-7.28-7.36s3.2-7.36 7.28-7.36c2.2 0 3.8.86 4.98 2l2.32-2.32C18.68 3.32 16.18 2 12.48 2 6.68 2 2 6.48 2 12s4.68 10 10.48 10c3.08 0 5.38-1.02 7.18-2.92 1.86-1.86 2.44-4.48 2.44-6.6 0-.66-.06-1.26-.16-1.76h-9.46z" fill="#4285F4"/>
  </svg>
);
const MetaAdsIcon = () => (
  <img src="/logo_meta.png" alt="Meta" className="w-4 h-4" />
);
const LinkedInAdsIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="#0A66C2"/>
  </svg>
);

interface AutomationRulesListProps {
  rules: AutomationRule[];
  isLoading: boolean;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  filterStatus: string;
  onFilterStatusChange: (status: string) => void;
  filterPlatform: string;
  onFilterPlatformChange: (platform: string) => void;
  onToggleStatus: (rule: AutomationRule) => void;
  onEdit: (rule: AutomationRule) => void;
  onDelete: (id: string) => void;
  onCreateNew: () => void;
  onOpenTemplates: () => void;
}

const AutomationRulesList: React.FC<AutomationRulesListProps> = ({
  rules, isLoading, searchTerm, onSearchChange, filterStatus, onFilterStatusChange,
  filterPlatform, onFilterPlatformChange, onToggleStatus, onEdit, onDelete,
  onCreateNew, onOpenTemplates
}) => {
  const { t } = useTranslation();

  const filteredRules = rules.filter(rule => {
    const matchesSearch = rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rule.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || rule.status === filterStatus;
    const matchesPlatform = filterPlatform === 'all' ||
      (Array.isArray(rule.platform) ? rule.platform.includes(filterPlatform) : rule.platform === filterPlatform);
    return matchesSearch && matchesStatus && matchesPlatform;
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('automation.filters.searchRules', 'Search rules...')}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="input pl-10"
            />
          </div>
          <select value={filterStatus} onChange={(e) => onFilterStatusChange(e.target.value)} className="input w-full sm:w-40">
            <option value="all">{t('automation.filters.allStatus', 'All status')}</option>
            <option value="active">{t('automation.filters.active', 'Active')}</option>
            <option value="paused">{t('automation.filters.paused', 'Paused')}</option>
          </select>
          <select value={filterPlatform} onChange={(e) => onFilterPlatformChange(e.target.value)} className="input w-full sm:w-40">
            <option value="all">{t('automation.filters.allPlatforms', 'All platforms')}</option>
            <option value="google">Google Ads</option>
            <option value="meta">Meta Ads</option>
            <option value="linkedin">LinkedIn Ads</option>
          </select>
        </div>
      </div>

      {/* Rules */}
      {isLoading ? (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-8 text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-primary-500 mx-auto" />
          <p className="mt-4 text-gray-500 dark:text-gray-400">{t('automation.loading', 'Loading...')}</p>
        </div>
      ) : filteredRules.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
            <Workflow className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {t('automation.empty.title', 'No automation rules')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
            {t('automation.empty.description', 'Create your first automation rule to optimize your campaigns.')}
          </p>
          <div className="flex items-center justify-center gap-3">
            <button onClick={onOpenTemplates} className="btn-secondary">
              {t('automation.empty.useTemplate', 'Use template')}
            </button>
            <button onClick={onCreateNew} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              {t('automation.empty.createRule', 'Create rule')}
            </button>
          </div>
        </div>
      ) : (
        filteredRules.map((rule) => (
          <div key={rule.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden hover:shadow-lg transition-all">
            <div className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                <div className="flex items-start gap-4 flex-1">
                  <div className={`p-3 rounded-xl ${
                    rule.status === 'active' ? 'bg-gradient-to-br from-green-400 to-green-600'
                    : rule.status === 'error' ? 'bg-gradient-to-br from-red-400 to-red-600'
                    : 'bg-gradient-to-br from-gray-400 to-gray-600'
                  }`}>
                    {rule.status === 'active' ? <Zap className="w-6 h-6 text-white" />
                    : rule.status === 'error' ? <AlertTriangle className="w-6 h-6 text-white" />
                    : <Pause className="w-6 h-6 text-white" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-gray-900 dark:text-white text-lg">{rule.name}</h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        rule.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                        : rule.status === 'error' ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {rule.status === 'active' ? t('automation.status.active', 'Active') : rule.status === 'error' ? t('automation.status.error', 'Error') : t('automation.status.paused', 'Paused')}
                      </span>
                      <span className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        {Array.isArray(rule.platform) && rule.platform.map((p: string) => (
                          <span key={p} className="flex items-center gap-1">
                            {p === 'google' ? <GoogleAdsIcon /> : p === 'meta' ? <MetaAdsIcon /> : <LinkedInAdsIcon />}
                          </span>
                        ))}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{rule.description}</p>

                    {/* Rule flow */}
                    <div className="flex items-center gap-2 mt-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                        <Target className="w-4 h-4 text-primary-500" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {getTriggerLabel(rule.trigger_type)} {rule.trigger_value}
                          {rule.trigger_type.includes('ctr') || rule.trigger_type.includes('roas') || rule.trigger_type.includes('spend') ? '%' : ''}
                        </span>
                      </div>
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                        <Zap className="w-4 h-4 text-amber-500" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {getActionLabel(rule.action_type)}
                          {rule.action_value ? ` (${rule.action_value > 0 ? '+' : ''}${rule.action_value}%)` : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 px-3 py-1.5 text-gray-500 dark:text-gray-400">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm">{getFrequencyLabel(rule.frequency)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 sm:gap-6">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{rule.executions_count}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('automation.rule.executions', 'Executions')}</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {rule.successful_executions && rule.executions_count ? Math.round((rule.successful_executions / rule.executions_count) * 100) : 0}%
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('automation.rule.success', 'Success')}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button onClick={() => onToggleStatus(rule)} className={`p-2.5 rounded-xl transition-all ${
                      rule.status === 'active'
                        ? 'bg-amber-100 text-amber-600 hover:bg-amber-200 dark:bg-amber-900/50 dark:text-amber-400'
                        : 'bg-green-100 text-green-600 hover:bg-green-200 dark:bg-green-900/50 dark:text-green-400'
                    }`}>
                      {rule.status === 'active' ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                    </button>
                    <button onClick={() => onEdit(rule)} className="p-2.5 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700">
                      <Edit className="w-5 h-5" />
                    </button>
                    <button onClick={() => onDelete(rule.id)} className="p-2.5 rounded-xl bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-400 dark:hover:bg-red-900">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {rule.last_execution && (
              <div className="px-6 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  <Clock className="w-4 h-4 inline-block mr-1" />
                  {t('automation.rule.lastExecution', 'Last execution')} {formatTimeAgo(rule.last_execution)}
                </span>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default AutomationRulesList;
